import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/lib/useUser";
import { orgCreditsView } from "@/functions/orgCreditsView";

/**
 * useAvailableCredits — resolves the SPENDABLE credit balance for the current user.
 *
 * For solo users this is simply their personal `monthly_credits` + `topup_credits`.
 * For ORG members, credits live in the organization's SHARED pool, so we fetch
 * `orgCreditsView` (which returns `org.shared_monthly_credits` /
 * `org.shared_topup_credits` under the `monthly` / `topup` fields) and report the
 * shared balance instead. This keeps the client-side credit gate and the global
 * credit display consistent with the (already org-aware) backend deduction.
 *
 * @returns {{
 *   monthly: number,
 *   topup: number,
 *   total: number,
 *   isOrg: boolean,
 *   isLoading: boolean,
 * }}
 *
 * Callers gating an action should treat `isLoading` as "unknown → allow" and let
 * the backend 402 be the authoritative gate, so we never falsely block an org
 * member while their shared balance is still in flight.
 */
export function useAvailableCredits() {
  const { user } = useUser();
  const isOrg = !!user?.org_id;

  const {
    data,
    isLoading: orgLoading,
  } = useQuery({
    // Keyed on org_id so a member switching orgs gets a fresh balance.
    queryKey: ["availableCredits", user?.org_id],
    queryFn: async () => {
      const { data: res, error } = await orgCreditsView();
      if (error) {
        // Surface as a thrown error so react-query records it; callers fall back
        // to the personal pool via the resolution below.
        throw new Error(error.error || error.message || "Failed to load org credits");
      }
      return res;
    },
    enabled: isOrg,
    staleTime: 45 * 1000, // ~45s — fresh enough post-generation, avoids hammering.
    retry: 1,
  });

  // Solo user (or org fetch errored): fall back to the personal pool.
  if (!isOrg || !data) {
    const monthly = user?.monthly_credits || 0;
    const topup = user?.topup_credits || 0;
    return {
      monthly,
      topup,
      total: monthly + topup,
      isOrg,
      isLoading: isOrg ? orgLoading : false,
    };
  }

  const monthly = data.monthly || 0;
  const topup = data.topup || 0;
  return {
    monthly,
    topup,
    total: typeof data.total === "number" ? data.total : monthly + topup,
    isOrg: true,
    isLoading: orgLoading,
  };
}
